// Copyright (c) 2012-2021 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved. NOTICE: All information contained herein
// is, and remains the property of GRAB. The intellectual and technical concepts contained herein are confidential, proprietary
// and controlled by GRAB and may be covered by patents, patents in process, and are protected by trade secret or copyright law.
//
// You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate, adapt or change this material
// in any way unless prior written permission is obtained from GRAB. Access to the source code contained herein is hereby
// forbidden to anyone except current GRAB employees or contractors with binding Confidentiality and Non-disclosure agreements
// explicitly covering such access.
//
// The copyright notice above does not evidence any actual or intended publication or disclosure of this source code,
// which includes information that is confidential and/or proprietary, and is a trade secret, of GRAB.
//
// ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR THROUGH USE OF THIS SOURCE
// CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE LAWS AND
// INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY
// OR IMPLY ANY RIGHTS TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING
// THAT IT MAY DESCRIBE, IN WHOLE OR IN PART.

import React, { CSSProperties } from 'react'
import { connect } from 'react-redux'
import { Button, Col, ConfigProvider, Modal, Row, Table, Typography, Menu, Space, Tooltip, message } from 'antd'
import { PlusOutlined, InfoCircleOutlined, SettingOutlined, UploadOutlined } from '@ant-design/icons'
import { sumBy } from 'lodash'
import './index.less'
import { FilterSwitch } from 'components/camera-directory/FilterSwitch'
import InitialPanel from 'pages/device-initialize/InitialPanel'
import { DirectoryFilter } from './Filter'
import DirectorySearch from './Search'
import classnames from 'classnames'
import DeviceDetails from './DeviceDetails'
import { Dispatch, RootState } from 'store'
import { IDirectoryFilter } from 'models/directory'
import { OnlineHoursCell } from '../../components/camera-details'
import { DeviceIdCell } from 'components/camera-directory/DeviceIdCell'
import { BatteryLevelCell } from '../../components/camera-directory/BatteryLevelCell'
import {
  CameraDirectoryAction, CameraStorage,
  DeviceID,
  DeviceType,
  IDevice,
  IDeviceWithLiteGroupInfo
} from '../../constants/device'
import TraceModal from '../../components/trace-modal'
import { DataUsageCell } from '../../components/camera-directory/DataUsageCell'
import DetailEditingModal from 'pages/camera-directory/DetailEditingModal'
import { EmptyResult } from 'components/empty-result'
import emptyImage from 'assets/images/devices-empty.png'
import { ContextMenu } from '../../components/context-menu'
import { GPSTrace } from '../../components/icon'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import { DOWNLOAD_TASK_TYPE } from '../../constants'
import { CameraTypeCell, OnlineStatusCell } from '../../components/camera-details'
import { ImageStorageCell } from '../../components/camera-details/ImageStorageCell'
import { LiteGroupTable } from './LiteGroupTable'
import { DeviceActions } from '../../components/camera-details/DeviceActions'
import { LiteGroupCameraTypeCell } from '../../components/camera-details/LiteGroupCameraTypeCell'
import { ColumnsType } from 'antd/lib/table/interface'
import { CameraContextMenu, CameraContextMenuAnchor } from './CameraContextMenu'
import { ComposedTitle } from 'components/composed-title'
import { city } from '../../models/city'
import UploadModal from '../../components/image-upload/UploadModal'
import moment from 'moment'
import { fetchDeviceIDs } from '../../api/devices'

const renderEmpty = () => (
  <div style={{ marginTop: 80, marginBottom: 80 }}>
    <EmptyResult image={emptyImage} desc='Check the spelling of your search and try again.' />
  </div>
)

function mapState(state: RootState) {
  return {
    cameraState: state.cameras,
    firmwareState: state.firmware,
    directoryState: state.directory,
    cities: state.city.cityList,
    uploadCameraLoading: state.loading.effects.download.createDownloadTask
  }
}

function mapDispatch(dispatch: Dispatch) {
  return {
    cameraAction: dispatch.cameras,
    firmwareAction: dispatch.firmware,
    directoryAction: dispatch.directory,
    fetchCities: dispatch.city.fetchCityList,
    setCurrentCity: dispatch.city.setCurrentCity,
    updateEditingPreTask: dispatch.download.updateEditingPreTask,
    createDownloadTask: dispatch.download.createDownloadTask,
  }
}

export type ICameraDirectoryProps = ReturnType<typeof mapState> &
  ReturnType<typeof mapDispatch> & {
  style: CSSProperties
} & RouteComponentProps

interface CameraContextMenuState {
  record?: IDevice;
  anchor: CameraContextMenuAnchor,
  excludeActions?: CameraDirectoryAction[]
}

export type ICameraDirectoryState = {
  initialPanelVisible: boolean
  filterVisible: boolean
  drawerVisible: boolean
  viewTraceDeviceId?: string
  editingModalVisible: boolean
  editingDeviceID: string
  editingGroupID?: number
  editingCityID?: number
  contextMenu?: CameraContextMenuState
  activeRowKey: string,
  expandedRowKeys: (string | number)[],
  uploadingCamera:IDevice | null
  midLat: number
  midLon: number
  gridPoints: any
  mapInitZoom: number
}

interface GetColumns<T> {
  (isMainTable: boolean): ColumnsType<T>
}
const initialValues = {
  uploadTime: [moment().hour(9).minute(0).seconds(0), moment().hour(18).minute(0).seconds(0)]
}


class CameraManagement extends React.Component<ICameraDirectoryProps, ICameraDirectoryState> {
  state: ICameraDirectoryState = {
    initialPanelVisible: false,
    filterVisible: true,
    drawerVisible: false,
    viewTraceDeviceId: undefined,
    editingModalVisible: false,
    editingDeviceID: null,
    editingGroupID: null,
    editingCityID: null,
    contextMenu: null,
    activeRowKey: null,
    expandedRowKeys: [],
    uploadingCamera: null,
    midLat: null,
    midLon: null,
    gridPoints: [],
    mapInitZoom: 10,
  }

  private contentRef = React.createRef<HTMLDivElement>()

  componentDidMount() {
    const { directoryAction, firmwareAction } = this.props
    // Javis debugging
    this.fetchDeviceIDsBySequence()

    directoryAction.loadPage({ page: 1, hasFilter: false })
    firmwareAction.fetchVersions()
    this.contentRef.current.addEventListener('wheel', this.handleContentScroll, { passive: false })
  }

  componentWillUnmount() {
    this.contentRef.current.removeEventListener('wheel', this.handleContentScroll)
    const { directoryAction } = this.props
    directoryAction.clear()
  }


  fetchDeviceIDsBySequence = async () => {
    const { location, history } = this.props

    const search = location.search;

    if (location.search) {
      var midLat, midLon

      const params = new URLSearchParams(search);
      const sequenceIDsString = params.get('sequenceIDs');
      const neLat = parseFloat(params.get('neLat'));
      const swLat = parseFloat(params.get('swLat'));
      const neLng = parseFloat(params.get('neLng'));
      const swLng = parseFloat(params.get('swLng'));

      var sequenceIDs = sequenceIDsString.split(',');

      midLat = neLat + swLat
      midLat = midLat/2
      midLon = neLng + swLng
      midLon = midLon/2

      const gridPoints = [
        {lat: neLat, lon: neLng},
        {lat: swLat, lon: neLng},
        {lat: swLat, lon: swLng},
        {lat: neLat, lon: swLng},
        {lat: neLat, lon: neLng},
      ]

      const query = {
        sequenceIDs: sequenceIDs
      }
      let deviceIDs = await fetchDeviceIDs(query)
      if (deviceIDs && deviceIDs.length > 0){
        this.setState({ mapInitZoom: 16 })
        this.handleViewDeviceTrace(deviceIDs[0])

        if (midLat && midLon && midLat != 0 && midLon != 0) {
          this.handleMidLocation(midLat, midLon)
          this.handleGrid(gridPoints)
        }

      } else {
        message.info('No sequence records')
        history.push({pathname: '/cameras'})
      }
    }

  }

  handleContentScroll = e => {
    const { contextMenu } = this.state
    if (contextMenu) {
      e.preventDefault()
    }
  }

  handleAddNewCameraClick = () => {
    this.setState({
      initialPanelVisible: true
    })
  }

  handleModalClose = () => {
    const { directoryAction } = this.props
    this.setState(
      {
        initialPanelVisible: false
      },
      () => {
        directoryAction.filterDevices()
      }
    )
  }

  handleViewDeviceTrace = (viewTraceDeviceId: string): void => {
    this.setState({ viewTraceDeviceId })
  }

  handleOnCloseTrace = () => {
    this.setState({ 
      viewTraceDeviceId: undefined,
      midLat: null,
      midLon: null,
      gridPoints: [],
      mapInitZoom: 10,
    })
    this.props.history.push({pathname: '/cameras'})
  }


  handleMidLocation = (midLat: number, midLon: number): void => {
    this.setState({ midLat, midLon })
  }

  handleGrid = (gridPoints): void => {
    this.setState({ gridPoints: [gridPoints] })
  }

  handleFilterSwitchClick = () => {
    this.setState({
      filterVisible: !this.state.filterVisible
    })
  }

  handleFilterChange = (values: Partial<IDirectoryFilter>) => {
    this.props.directoryAction.filterDevices(values)
    this.contentRef.current.scrollTo(0, 0)
  }

  handleTableRowClick = (e, deviceID: DeviceID) => {
    const { directoryAction } = this.props
    this.setState({
      drawerVisible: true,
      activeRowKey: deviceID
    })
    directoryAction.fetchDetail(deviceID).catch(err => {
      if (err.response.status === 404) {
        Modal.error({
          content: 'Not found the camera'
        })
      }
    })
  }

  handleDetailClose = () => {
    this.setState({
      drawerVisible: false
    })
  }

  handleEdit = (deviceID: string, groupID?: number, cityID?: number) => {
    this.setState({
      editingDeviceID: deviceID,
      editingGroupID: groupID,
      editingCityID: cityID,
      editingModalVisible: true
    })
  }

  handleEditingSuccess = () => {
    const { directoryAction } = this.props
    this.setState(
      {
        editingDeviceID: null,
        editingGroupID: null,
        editingCityID: null,
        editingModalVisible: false,
        drawerVisible: false,
        // expandedRowKeys: []
      },
      () => {
        directoryAction.filterDevices()
      }
    )
  }

  handleEditingClose = () => {
    this.setState({
      editingDeviceID: null,
      editingGroupID: null,
      editingCityID: null,
      editingModalVisible: false
    })
  }

  handleTableRowContextMenu = (event: React.MouseEvent, record: IDevice) => {
    event.preventDefault()
    this.setState({
      contextMenu: {
        anchor: {
          x: event.clientX,
          y: event.clientY
        },
        record
      },
      activeRowKey: record.deviceID
    })
  }

  handleContextMenuDismiss = () => {
    this.setState({ contextMenu: null, activeRowKey: null })
  }
  handleCreateUploadTask = (device: IDevice) => {
    this.setState({uploadingCamera: device})
    // this.props.history.push({ pathname: '/upload', state: { taskType: DOWNLOAD_TASK_TYPE.DEVICE, ...device } })
  }
  handleContextMenuAction = (action: CameraDirectoryAction) => {
    const { contextMenu } = this.state
    const { record } = contextMenu
    if (!record) {
      return
    }
    this.handleCameraAction(action, contextMenu.record)
    this.setState({ contextMenu: null })
  }
  handlePageChange = (page: number) => {
    const { directoryAction } = this.props
    directoryAction.loadPage({
      page,
      hasFilter: true
    })
  }

  renderExpandedRow = (sumOfColumnsWidth: number) => (record: IDevice, index: number, indent: number, expanded: boolean) => {
    return (
      <LiteGroupTable
        device={record}
        key={`lite-role-${index}`}
        scroll={{ x: sumOfColumnsWidth }}
        highlightRowId={record.deviceID}
        onRow={(record) => {
          return {
            onClick: event => this.handleTableRowClick(event, record.deviceID),
            onContextMenu: event => this.handleTableRowContextMenu(event, record)
          }
        }}
        getColumns={() => this.getColumns(false)}
      />
    )
  }

  getColumns: GetColumns<IDevice> = (isMainTable: boolean) => {
    return [
      {
        title: 'CameraID',
        key: 'deviceID',
        width: 170,
        fixed: true,
        render: (_, record) => {
          return <DeviceIdCell
            testMode={record.testMode && record.deviceType !== DeviceType.Lite360Slave}
            deviceID={record.deviceID} />
        }
      },
      {
        title: isMainTable ? 'Camera Type' : '360Lite member',
        width: isMainTable ? 150 : 197,
        key: 'deviceType',
        ellipsis: true,
        render: (text, d) => {
          if (isMainTable) {
            return <CameraTypeCell type={d.deviceType} />
          } else {
            const record = d as IDeviceWithLiteGroupInfo
            return <LiteGroupCameraTypeCell role={record.role} direction={record.direction} />
          }
        }
      },
      {
        title: 'Camera Free Storage',
        key: 'freeStorage',
        width: 170,
        ellipsis: true,
        render(_, record) {
          //todo:render image storage
          const {totalImageCount,freeStorage} = record
          const percent = Number((freeStorage / CameraStorage * 100).toFixed(0))
          return <ImageStorageCell percent={percent} imageCount={totalImageCount} />
        }
      },
      {
        title: 'Camera Stored Images',
        key: 'imageStored',
        width: 170,
        ellipsis: true,
        render(_, record) {
          //todo:render image storage
          const {totalImageCount} = record
          return <div>{totalImageCount} img</div>
        }
      },
      {
        title: 'Driver',
        key: 'driverID',
        width: 170,
        ellipsis: true,
        render(_, record) {
          //todo:render driver name
          const {driverID} = record
          const driver = driverID || '---'
          return <div>{driver}</div>
        }
      },
      {
        title: 'Camera Status',
        key: 'deviceStatus',
        width: 170,
        ellipsis: true,
        render(_, record) {
          //todo: render combined status
          return <OnlineStatusCell onlineStatus={record.onlineStatus} />
        }
      },
      {
        title: 'Actions',
        width: 170,
        ellipsis: true,
        // fixed: 'right',
        render: (_, record) => {
          const isSlaveCamera = record.deviceType === DeviceType.Lite360Slave
          const actions =
            isSlaveCamera
              ? []
              : [CameraDirectoryAction.ChangeSettings, CameraDirectoryAction.ViewTracks, CameraDirectoryAction.CreateTask]
          return (
            <DeviceActions
              actions={actions}
              onAction={(action) => this.handleCameraAction(action, record)}
            />
          )

        }
      }
    ]
  }

  handleCameraAction(action: CameraDirectoryAction, record: IDevice) {
    const { deviceID, groupID, cityID } = record
    switch (action) {
      case CameraDirectoryAction.ViewDetails:
        this.handleTableRowClick(null, deviceID)
        break
      case CameraDirectoryAction.ChangeSettings:
        this.handleEdit(deviceID, groupID, cityID)
        break
      case CameraDirectoryAction.ViewTracks:
        this.handleViewDeviceTrace(deviceID)
        break
      case CameraDirectoryAction.CreateTask:
        this.handleCreateUploadTask(record)
        break
      default:
        break
    }
  }

  getRowClassName = (record: IDevice, index) => {
    const { activeRowKey } = this.state
    if (record.deviceID === activeRowKey) {
      return 'ant-table-row-selected'
    }
  }
  handleUpload = () => {
    const {updateEditingPreTask, createDownloadTask,history,cities,setCurrentCity} = this.props
    const {uploadingCamera} = this.state
    const currentCity = cities.find(item => item.id === uploadingCamera.cityID)
    Modal.confirm({
      title: 'Are you sure you want to upload images from camera?',
      content: '',
      onOk: () => {
        return createDownloadTask()
          .then(() => {
            updateEditingPreTask(null)
            this.setState({ uploadingCamera: null})
            Modal.success({
              title: 'Submit Success',
              content: 'You can check the process in Upload Pending List',
              onOk: () => {
                setCurrentCity(currentCity)
                history.push({pathname: '/uploadqueue'})
              },
              okText: 'Go to Upload Pending List',
              cancelText: 'Back',
              maskClosable: true
            })
          })
          .catch(err => {
            message.error(err.toString())
          })
      }
    })
  }
  handleUploadModalChange =  async (action)=>{
    const {type, payload} = action
    const {updateEditingPreTask, createDownloadTask,history,cities,setCurrentCity} = this.props
    const {uploadingCamera} = this.state
    switch (type){
      case 'cancel':
        this.setState({ uploadingCamera: null})
        break
      case 'ok':
        updateEditingPreTask({...uploadingCamera,...payload})
        this.handleUpload()
        break
      default: break
    }
  }

  render() {
    const { firmwareState, directoryState, style ,uploadCameraLoading} = this.props
    //const deviceListWithoutSlave = directoryState.deviceList.filter(item=> item.deviceType !== DeviceType.Lite360Slave)
    const {
      initialPanelVisible,
      filterVisible,
      drawerVisible,
      editingModalVisible,
      editingDeviceID,
      editingGroupID,
      editingCityID,
      viewTraceDeviceId,
      expandedRowKeys,
      uploadingCamera,
      midLat,
      midLon,
      gridPoints,
      mapInitZoom
    } = this.state

    const filterClassname = classnames('directory-filter-wrap', {
      invisible: !filterVisible
    })
    const columns = this.getColumns(true)
    const sumOfColumnsWidth = sumBy(columns, 'width')
    return (
      <div className='directory' style={style || {}}>
        <InitialPanel visible={initialPanelVisible} onOk={this.handleModalClose} onCancel={this.handleModalClose} />
        {editingModalVisible ? (
          <DetailEditingModal
            device={{ deviceID: editingDeviceID, groupID: editingGroupID, cityID: editingCityID }}
            visible={editingModalVisible}
            onOk={this.handleEditingSuccess}
            onCancel={this.handleEditingClose}
          />
        ) : null}
        <Row style={{ flexWrap: 'nowrap' }}>
          <Col flex={1} style={{ width: 'calc(100% - 400px)' }}>
            <div className='directory-main' ref={this.contentRef}>
              <Row className='directory-header' justify='space-between'>
                <Col>
                  <Typography.Title level={3}>Camera Directory</Typography.Title>
                </Col>
                <Col>
                  <Button onClick={this.handleAddNewCameraClick}>
                    <PlusOutlined />
                    Add New Camera
                  </Button>
                </Col>
              </Row>
              <DirectorySearch />
              <Row className='directory-content'>
                <Col className={filterClassname}>
                  <DirectoryFilter
                    firmwareVersions={firmwareState.versions}
                    onClose={this.handleFilterSwitchClick}
                    values={directoryState.filter}
                    onChange={this.handleFilterChange}
                  />
                </Col>
                <Col style={{ flex: 1, width: 'calc(100% - 400px)' }}>
                  <Row justify='space-between' style={{ marginBottom: 16 }}>
                    <Col>Showing {directoryState.page.total} Results</Col>
                    <Col>
                      <FilterSwitch actived={filterVisible} onChange={this.handleFilterSwitchClick} />
                    </Col>
                  </Row>
                  <ConfigProvider renderEmpty={renderEmpty}>
                    <Table
                      className='directory-table'
                      tableLayout='fixed'
                      rowKey={(record: IDevice) => record.deviceID}
                      dataSource={directoryState.deviceList}
                      pagination={{ ...directoryState.page, showSizeChanger: false, onChange: this.handlePageChange }}
                      loading={directoryState.loading}
                      columns={columns}
                      expandable={
                        {
                          indentSize: 0,
                          expandIconColumnIndex: 2,
                          rowExpandable: (record) => !!record.groupID,
                          onExpandedRowsChange: (expandedRowKeys) => this.setState({ expandedRowKeys }),
                          expandedRowKeys,
                          expandedRowClassName: () => 'camera-table-expanded-row',
                          expandedRowRender: this.renderExpandedRow(sumOfColumnsWidth)
                        }
                      }
                      onRow={(record: IDevice) => {
                        return {
                          onClick: event => this.handleTableRowClick(event, record.deviceID),
                          onContextMenu: event => this.handleTableRowContextMenu(event, record)
                        }
                      }}
                      scroll={{ x: sumOfColumnsWidth }}
                      rowClassName={this.getRowClassName}
                    />
                  </ConfigProvider>
                </Col>
              </Row>
            </div>
          </Col>
          <Col style={{ display: drawerVisible ? 'block' : 'none', flexShrink: 0, flexBasis: 400 }}>
            <DeviceDetails
              onClose={this.handleDetailClose}
              onViewTrace={this.handleViewDeviceTrace}
              onClickEdit={this.handleEdit}
            />
          </Col>
        </Row>
        {!!viewTraceDeviceId && (
          <TraceModal 
            deviceId={viewTraceDeviceId} 
            midLocation={{midLat: midLat, midLon: midLon}} 
            gridPoints={gridPoints}
            mapInitZoom={mapInitZoom}
            onClose={this.handleOnCloseTrace} 
          />
        )}
        {this.getContextMenu()}
        <UploadModal
          title={uploadingCamera?.deviceID}
          visible={!!uploadingCamera}
          onChange={this.handleUploadModalChange}
          initialValues={initialValues}
          destoryOnClose={true}
          // okButtonProps={{loading: uploadCameraLoading}}
        />
      </div>
    )
  }

  getContextMenu() {
    const { contextMenu } = this.state
    if (!contextMenu) {
      return
    }
    const device = contextMenu.record as IDevice
    if (!device) {
      return
    }
    //Only 2w 4w 360liteMaster can have upload button
    const excludeActions = []
    if (device.deviceType === DeviceType.Lite360Slave) {
      excludeActions.push(CameraDirectoryAction.ChangeSettings)
      excludeActions.push(CameraDirectoryAction.CreateTask)
    }
    return (
      <CameraContextMenu
        visible={true}
        excludeActions={excludeActions}
        anchor={contextMenu.anchor}
        onClick={this.handleContextMenuAction}
        onDismiss={this.handleContextMenuDismiss}
      />
    )
  }
}

export default withRouter(connect(mapState, mapDispatch)(CameraManagement))
